import { Dialog, DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  OnDestroy,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { AlertService, GamesService } from '@app/core';

import { CustomValidators, notNull, safeEmptyList } from '@app/shared/utils';
import { AppSelectors } from '@app/store';
import { AppState } from '@app/store/app.state';
import { Store } from '@ngrx/store';
import { size } from 'lodash';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  share,
  startWith,
  switchMap,
  tap,
} from 'rxjs/operators';

import { ModalGamesComponent } from '@app/layouts/components/modal-games/modal-games.component';
import { SHARED_MODULES } from '@app/shared';
import { ModalAddTrailComponent } from '../../pages/posts/components/modal-add-trail/modal-add-trail.component';
import { ModalAddTrailService } from '../../pages/posts/components/modal-add-trail/modal-add-trail.service';
import { FormBase, ModalDailyData, ModalDailyTab, SidenavDailyShow } from './base';
import { FormActivityComponent } from './form-activity.component';
import { FormDailyComponent } from './form-daily.component';
import { SidenavDailyComponent } from './sidenav-daily.component';
import { SidenavEvaluationComponent } from './sidenav-evaluation.component';

@Component({
  imports: [SHARED_MODULES, SidenavEvaluationComponent, SidenavDailyComponent],
  templateUrl: './modal-daily.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalDailyComponent implements OnDestroy {
  private readonly _dialog = inject(Dialog);
  private readonly _dialogRef = inject(DialogRef);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _store = inject(Store<AppState>);
  private readonly _cdref = inject(ChangeDetectorRef);
  private readonly _gamesService = inject(GamesService);
  private readonly _alertService = inject(AlertService);
  public readonly data = inject<ModalDailyData>(DIALOG_DATA);
  public readonly modalAddTrailService = inject(ModalAddTrailService);
  public readonly tab = ModalDailyTab;
  public publishCtrl = new FormControl(true);
  public form$: Observable<any>;
  public instance$: Observable<FormBase>;
  public targetsCtrl = new FormControl<SidenavDailyShow[]>([], [CustomValidators.nonEmpty]);
  public selectedTrailElements$ = new BehaviorSubject<any[]>([]);
  public submitted$: Observable<boolean>;
  public isBrazil = signal<boolean>(false);
  public isExternalHispanic = signal<boolean>(false);
  public gamesForm: FormGroup;
  public loadingGameSeries = signal<boolean>(true);
  public gameSeries$: Observable<any>;
  public gameSeries = signal<any>([]);
  public loadingGameDisciplines = signal<boolean>(false);
  public gameDisciplines = signal<any>([]);
  public loadingGames = signal<boolean>(false);
  public games = signal<any[]>([]);
  public selectedGames = signal<any[]>([]);
  public switchGames = signal<boolean>(false);
  public materiais = signal<boolean>(false);
  private _formEdit: FormGroup;
  private _selectedForm: SidenavDailyShow;
  private _instance$ = new Subject<FormBase>();
  private _submitted$ = new BehaviorSubject<boolean>(false);
  private _config$ = new Subject<ModalDailyData>();
  @ViewChild(SidenavDailyComponent, { static: false }) public daily: SidenavDailyComponent;

  constructor() {
    this._store
      .select(AppSelectors.isExternalHispanic)
      .pipe(notNull())
      .subscribe((isExternal) => this.isExternalHispanic.set(isExternal));

    this.instance$ = this._instance$.asObservable();
    const instanceBlock$ = this._instance$.pipe(
      notNull(),
      tap((instance) => this.patchValue(instance, this.data)),
      switchMap((instance) => instance.submitted$),
      share(),
    );
    this.submitted$ = combineLatest([this._submitted$, instanceBlock$]).pipe(
      takeUntilDestroyed(this._destroyRef),
      map(([submitted, instance]) => submitted || instance),
      distinctUntilChanged(),
      debounceTime(300),
      tap(() => this._cdref.detectChanges()),
    );
    this.loadTargets();
    this.form$ = this._config$.asObservable().pipe(
      startWith(this.data),
      map((config) => this.loadForms(config)),
    );

    this._store
      .select(AppSelectors.isBrasil)
      .pipe(takeUntilDestroyed(this._destroyRef), notNull())
      .subscribe((isBrazil) => this.isBrazil.set(isBrazil));

    this.loadGamesForm();
  }

  public get isEdit(): boolean {
    return !!this.data?.data?.data?.id;
  }

  public ngOnDestroy() {
    this.selectedTrailElements$.complete();
    this._config$.complete();
    this._instance$.complete();
    this._submitted$.complete();
  }

  public onSave(config: any) {
    if (this.isEdit) {
      this.update(config);
    } else {
      this.store(config);
    }
  }

  public onTargetForm(form: FormGroup) {
    this._formEdit = form;
  }

  public onSelectedForm(data: SidenavDailyShow) {
    this._selectedForm = data;
  }

  public onTargets(data: SidenavDailyShow[] = []) {
    this.targetsCtrl.reset(data);
  }

  public onAttached(component: any) {
    this._instance$.next(component.instance);
  }

  public showTabs(data: any[]): boolean {
    return size(data) > 1;
  }

  public showError(ctrl: FormControl): boolean {
    return ctrl.invalid && ctrl.touched;
  }

  public tooltip(data: SidenavDailyShow): string {
    return `${data.grade.description} - ${data.group.description} - ${data.subject.description}`;
  }

  public onClose(instance: FormBase) {
    this.preventLostData(instance, () => this._dialogRef.close());
  }

  public onClickSwitch(event: boolean, type) {
    if (type === 'games') {
      this.initGamesCard(event);
    }
    if (type === 'materiais') {
      this.materiais.set(event);
    }
  }

  public onOpenGame({ path, name }) {
    this._dialog
      .open(ModalGamesComponent, { data: { gamePath: path, gameTitle: name } })
      .closed.pipe(takeUntilDestroyed(this._destroyRef), notNull());
  }

  public onCheckGame(event: any, config: any, gameId: string) {
    if (event.target.checked) {
      this.selectedGames.set([...this.selectedGames(), gameId]);
    } else {
      this.selectedGames.set(this.selectedGames().filter((gId) => gId !== gameId));
    }
    config.instance.form.controls.games.setValue(this.selectedGames());
  }

  public isGameChecked(gameId: string) {
    return this.selectedGames().some((gId) => gId === gameId);
  }

  public onOpenModalAddTrail(config: any) {
    this._dialog
      .open<boolean>(ModalAddTrailComponent, {
        data: {
          selectedElements: this.selectedTrailElements$.value,
        },
      })
      .closed.pipe(
        notNull(),
        filter((data: any) => !!data?.elements),
        tap((data) => {
          this.selectedTrailElements$.next(data.elements);
          config.instance.form.controls.learningTrailsElements.setValue(data.elements);
          this.modalAddTrailService.clearSelectedElements();
        }),
      )
      .subscribe();
  }

  public onRemoveElement(element: any, config: any) {
    const elements = this.selectedTrailElements$.value.filter((el) => el.id !== element.id);
    this.selectedTrailElements$.next(elements);
    config.instance.form.controls.learningTrailsElements.setValue(elements);
  }

  public getStyleTextIcon(type: string): string {
    switch (type) {
      case 'avaliacao':
        return 'text-base-trilhas-tipo-quiz';
      case 'imagem':
        return 'text-base-trilhas-tipo-image';
      case 'flip':
        return 'text-base-trilhas-tipo-ebook';
      case 'texto':
        return 'text-base-trilhas-tipo-text';
      default:
        return `text-base-trilhas-tipo-${type}`;
    }
  }

  public mapTypeToIcon(element: string): string {
    const type = element.toLowerCase();

    const typeMapping: Record<string, string> = {
      audio: 'icon-headphone.svg',
      avaliacao: 'icon-book-pencil.svg',
      flip: 'icon-books.svg',
      imagem: 'icon-landscape.svg',
      link: 'icon-clip.svg',
      texto: 'icon-text-lines.svg',
      video: 'icon-film-alt.svg',
    };

    return typeMapping[type] || 'icon-help.svg';
  }

  private initGamesCard(showCard = false) {
    this.switchGames.set(showCard);
    if (!!showCard) {
      this.loadGameSeries();
      this.loadGameDisciplines();
      this.loadGames();
    } else {
      this.selectedGames.set([]);
      this.gamesForm.reset();
    }
  }

  private loadGames(data: { serie: number; discipline: number } = { serie: null, discipline: null }) {
    if (this.switchGames()) {
      this.loadingGames.set(true);

      this._gamesService
        .games(data)
        .pipe(
          safeEmptyList(),
          finalize(() => this.loadingGames.set(false)),
        )
        .subscribe((games: any) => this.games.set(games));
    }
  }

  private loadGameSeries() {
    this.loadingGameSeries.set(true);
    this._gamesService
      .series()
      .pipe(finalize(() => this.loadingGameSeries.set(false)))
      .subscribe((data: any) => this.gameSeries.set(data));
  }

  private loadGameDisciplines() {
    this.loadingGameDisciplines.set(true);
    this._gamesService
      .discipline()
      .pipe(finalize(() => this.loadingGameDisciplines.set(false)))
      .subscribe((data: any) => this.gameDisciplines.set(data));
  }

  private loadGamesForm() {
    this.gamesForm = new FormGroup({
      serie: new FormControl(undefined),
      discipline: new FormControl(undefined),
    });

    this.gamesForm.valueChanges.subscribe(({ serie, discipline }) => this.loadGames({ serie, discipline }));
  }

  private loadTargets() {
    const targets$ = this.targetsCtrl.valueChanges.pipe(startWith(this.targetsCtrl.value));
    combineLatest([this.instance$, targets$])
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(([instance, target]) => (instance.targets = target));
  }

  private preventLostData(instance: FormBase, action: () => void) {
    if (instance.form.dirty) {
      this._alertService
        .confirm({ message: 'geral.alertas.deseja-realmente-sair' })
        .pipe(notNull())
        .subscribe(() => action());
    } else {
      action();
    }
  }

  private loadForms(config: ModalDailyData): any {
    const forms = {
      [ModalDailyTab.Daily]: FormDailyComponent,
      [ModalDailyTab.Activity]: FormActivityComponent,
    };
    return {
      ...config,
      component: new ComponentPortal(forms[config.current] as any),
    };
  }

  private patchValue(instance: FormBase, modal: ModalDailyData) {
    instance.setLegacy(modal?.isLegacy);

    if (modal?.data?.data) {
      this.onTargets([modal.data.target]);
      instance.patchValue(modal.data.data, modal.data.target);
      this.publishCtrl.setValue(!!modal.data.data.published);

      if (modal?.data?.data?.trail_elements) {
        this.selectedTrailElements$.next(modal.data.data.trail_elements);
      }

      if (modal?.data?.data?.games?.length > 0) {
        this.initGamesCard();
        this.selectedGames.set(modal.data.data.games.map((g) => g.id));
      }
    }
  }

  private store(config: any) {
    if (!config.submitted) {
      if (config.instance.form.valid && this.targetsCtrl.valid) {
        this._submitted$.next(true);
        this.save(config, this.targetsCtrl.value);
      } else {
        this._alertService.alert({ message: 'geral.alertas.formulario-invalido' });
        this.targetsCtrl.markAsTouched();
        config.instance.markAllAsTouched();
      }
    }
  }

  private update(config: any) {
    if (!config.submitted) {
      const form = this.daily?.form || this._formEdit;
      if (config.instance.form.valid && form.valid) {
        this._submitted$.next(true);
        const data = {
          ...config.form.data.target,
          ...this._selectedForm,
          date: form.value.date,
        };
        this.save(config, [data]);
      } else {
        this._alertService.alert({ message: 'geral.alertas.formulario-invalido' });
        this.daily?.markAllAsTouched();
        config.instance.markAllAsTouched();
      }
    }
  }

  private save(config: any, targets: SidenavDailyShow[]) {
    config.instance
      .onSave({
        form: config.instance.form.value,
        publish: this.publishCtrl.value,
        targets,
      })
      .pipe(
        tap(() => this._submitted$.next(false)),
        notNull(),
      )
      .subscribe(() => {
        this.modalAddTrailService.clearSelectedElements();
        this._dialogRef.close(true);
      });
  }
}
